import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import { getPointOfSales } from '../../../services/pointOfSalesService';
import { salesCashLiquidationService } from '../../../services/salesCashLiquidationService';
import { downloadCashLiquidationPdf } from '../../../utils/generateCashLiquidationPdf';
import {
  getVentasPaymentMethodLabel,
  VENTAS_PAYMENT_METHODS,
} from '../../../utils/ventasSalesConstants';

const STORAGE_KEY = 'ventas_cash_liquidation_filters';

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatMoney(amount) {
  return `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return '—';
  const [y, m, d] = String(value).split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}/${y}`;
}

function loadStoredFilters(fallbackPosId = '') {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { shiftDate: todayLocal(), pointOfSaleId: fallbackPosId || '' };
    }
    const parsed = JSON.parse(raw);
    return {
      shiftDate: parsed.shiftDate || todayLocal(),
      pointOfSaleId: parsed.pointOfSaleId || fallbackPosId || '',
    };
  } catch {
    return { shiftDate: todayLocal(), pointOfSaleId: fallbackPosId || '' };
  }
}

function saveStoredFilters(filters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

function MethodRow({ byMethod }) {
  const map = {};
  (byMethod || []).forEach((row) => {
    map[row.payment_method] = row;
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {VENTAS_PAYMENT_METHODS.map((opt) => {
        const row = map[opt.value] || { orders_count: 0, total_amount: 0 };
        return (
          <div key={opt.value} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{opt.label}</p>
            <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(row.total_amount)}</p>
            <p className="text-xs text-gray-500 mt-0.5">{row.orders_count || 0} venta(s)</p>
          </div>
        );
      })}
    </div>
  );
}

export default function VentasCashLiquidationIndex() {
  const { loaded, ventasCashLiquidationEnabled } = useModuleSettings();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const stored = loadStoredFilters(selectedPointOfSale?.id ? String(selectedPointOfSale.id) : '');
    const posFromUrl = searchParams.get('pos');
    const dateFromUrl = searchParams.get('date');
    return {
      shiftDate: dateFromUrl || stored.shiftDate,
      pointOfSaleId: posFromUrl || stored.pointOfSaleId,
    };
  });
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [data, setData] = useState(null);
  const [openingBalance, setOpeningBalance] = useState('0.00');
  const [openingEditable, setOpeningEditable] = useState(true);
  const [openingFromDate, setOpeningFromDate] = useState(null);
  const [showOpeningSetup, setShowOpeningSetup] = useState(false);
  const [showAdjustmentSetup, setShowAdjustmentSetup] = useState(false);
  const [cashAdjustment, setCashAdjustment] = useState('0.00');
  const [notes, setNotes] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    if (!ventasCashLiquidationEnabled) return undefined;
    getPointOfSales()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setPointsOfSale(res.data.filter((p) => p.is_active !== false));
        }
      })
      .catch(() => {});
    return undefined;
  }, [ventasCashLiquidationEnabled]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!ventasCashLiquidationEnabled || !filters.pointOfSaleId || !filters.shiftDate) {
      setData(null);
      return null;
    }
    setLoading(true);
    try {
      const res = await salesCashLiquidationService.get({
        point_of_sale_id: filters.pointOfSaleId,
        shift_date: filters.shiftDate,
      });
      if (res.success) {
        setData(res.data);
        const liq = res.data.liquidation || {};
        setOpeningBalance(
          liq.opening_balance != null ? Number(liq.opening_balance).toFixed(2) : '0.00'
        );
        setOpeningEditable(liq.opening_balance_editable !== false);
        setOpeningFromDate(liq.opening_from_shift_date || null);
        setShowOpeningSetup(false);
        setShowAdjustmentSetup(false);
        setCashAdjustment(
          liq.cash_adjustment != null ? Number(liq.cash_adjustment).toFixed(2) : '0.00'
        );
        setNotes(liq.notes || '');
        return res.data;
      }
      setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      return null;
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar la liquidación',
        type: 'error',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [filters, ventasCashLiquidationEnabled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = data?.sales_summary;
  const cashSales = Number(summary?.cash_sales || 0);
  const openingNum = Number(openingBalance || 0);
  const adjustmentNum = Number(cashAdjustment || 0);
  const systemClosing = Math.round((openingNum + cashSales + adjustmentNum) * 100) / 100;
  const hasAdjustment = Math.abs(adjustmentNum) >= 0.01;
  const liquidationSaved = Boolean(data?.liquidation?.id);

  const handleDownloadReport = async (reportData = data) => {
    if (!reportData) {
      setToast({ isOpen: true, message: 'Consulte el día antes de generar el reporte', type: 'error' });
      return;
    }
    setPdfDownloading(true);
    try {
      await downloadCashLiquidationPdf(reportData);
      setToast({ isOpen: true, message: 'Reporte de cierre descargado', type: 'success' });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.message || 'No se pudo generar el reporte PDF',
        type: 'error',
      });
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!ventasCashLiquidationEnabled) return;
    if (!filters.pointOfSaleId || !filters.shiftDate) {
      setToast({ isOpen: true, message: 'Seleccione POS y fecha', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        point_of_sale_id: Number(filters.pointOfSaleId),
        shift_date: filters.shiftDate,
        cash_adjustment: Number(cashAdjustment || 0),
        closing_balance: systemClosing,
        notes: notes.trim() || null,
      };
      if (openingEditable) {
        payload.opening_balance = Number(openingBalance || 0);
      }
      const res = await salesCashLiquidationService.save(payload);
      if (res.success) {
        const fresh = await loadData();
        setToast({
          isOpen: true,
          message: 'Liquidación cerrada. Generando reporte oficial…',
          type: 'success',
        });
        if (fresh) {
          await handleDownloadReport(fresh);
        }
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo guardar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al guardar la liquidación',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const posLabel = data?.point_of_sale
    ? `${data.point_of_sale.code} — ${data.point_of_sale.name}`
    : '—';

  if (loaded && !ventasCashLiquidationEnabled) {
    return <Navigate to="/ventas/orders" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1200px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">
              Dashboard
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/orders" className="hover:text-teal-600">
              Ventas
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Liquidación de caja</span>
          </nav>

          <div className="mb-5">
            <h1 className="text-3xl font-bold text-gray-800">Liquidación de caja</h1>
            <p className="text-gray-600 mt-1 max-w-3xl">
              Resume las ventas del día, separa efectivo de pagos digitales y cuadra el
              saldo inicial / final de caja.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fecha del turno</label>
                <input
                  type="date"
                  value={filters.shiftDate}
                  onChange={(e) => updateFilter('shiftDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Punto de venta</label>
                <select
                  value={filters.pointOfSaleId}
                  onChange={(e) => updateFilter('pointOfSaleId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Seleccionar POS…</option>
                  {pointsOfSale.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.code} — {pos.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={loadData}
                  disabled={loading || !filters.pointOfSaleId}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
                >
                  {loading ? 'Cargando…' : 'Consultar día'}
                </button>
              </div>
            </div>
          </div>

          {!filters.pointOfSaleId ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500">
              Seleccione un punto de venta para liquidar la caja del día.
            </div>
          ) : loading && !data ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
              Cargando liquidación…
            </div>
          ) : data ? (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Resumen del día · {formatDate(data.shift_date)}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">{posLabel}</p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      data.shift_status === 'closed'
                        ? 'bg-gray-800 text-white'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    Turno {data.shift_status === 'closed' ? 'cerrado' : 'abierto'}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
                  <div className="rounded-lg bg-teal-50 border border-teal-100 px-4 py-3">
                    <p className="text-xs text-teal-700 font-medium">Ventas del día</p>
                    <p className="text-2xl font-bold text-teal-900 mt-1">
                      {formatMoney(summary?.total_amount)}
                    </p>
                    <p className="text-xs text-teal-700/80 mt-1">
                      {summary?.orders_count || 0} comprobante(s) · Neto{' '}
                      {formatMoney(summary?.base_imponible)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
                    <p className="text-xs text-emerald-700 font-medium">Efectivo cobrado</p>
                    <p className="text-2xl font-bold text-emerald-900 mt-1">
                      {formatMoney(summary?.cash_sales)}
                    </p>
                    <p className="text-xs text-emerald-700/80 mt-1">Entra a caja física</p>
                  </div>
                  <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3">
                    <p className="text-xs text-indigo-700 font-medium">Pagos digitales</p>
                    <p className="text-2xl font-bold text-indigo-900 mt-1">
                      {formatMoney(summary?.digital_total)}
                    </p>
                    <p className="text-xs text-indigo-700/80 mt-1">Yape / Plin / Transferencia</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
                    <p className="text-xs text-gray-600 font-medium">Otro</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatMoney(summary?.other_total)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Otros medios</p>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-gray-700 mb-2">Desglose por medio de pago</h3>
                <MethodRow byMethod={summary?.by_method} />
              </div>

              {openingEditable ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold text-amber-900">
                        Configuración inicial de caja
                      </h2>
                      <p className="text-sm text-amber-800/90 mt-1">
                        El saldo inicial solo se define una vez en este POS. Después se arrastra solo
                        desde el saldo final del día anterior.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowOpeningSetup((v) => !v)}
                      className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                    >
                      {showOpeningSetup ? 'Ocultar' : 'Configurar saldo inicial'}
                    </button>
                  </div>
                  {showOpeningSetup ? (
                    <div className="mt-4 max-w-sm">
                      <label className="block text-sm font-medium text-amber-900 mb-1">
                        Saldo inicial (primera vez)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={openingBalance}
                        onChange={(e) => setOpeningBalance(e.target.value)}
                        className="w-full px-3 py-2.5 border border-amber-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                      <p className="text-xs text-amber-800/80 mt-1">
                        Dinero con el que arranca la caja. Luego no se edita en el arqueo diario.
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-sky-900">Ajuste de caja</h2>
                    <p className="text-sm text-sky-800/90 mt-1">
                      Úselo solo si la caja se descuadra (faltante o sobrante). No forma parte del
                      arqueo diario habitual.
                    </p>
                    {hasAdjustment ? (
                      <p className="text-xs font-medium text-sky-900 mt-2">
                        Ajuste actual: {formatMoney(adjustmentNum)}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAdjustmentSetup((v) => !v)}
                    className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium border border-sky-300 bg-white text-sky-900 hover:bg-sky-100"
                  >
                    {showAdjustmentSetup ? 'Ocultar' : 'Registrar ajuste'}
                  </button>
                </div>
                {showAdjustmentSetup ? (
                  <div className="mt-4 max-w-sm">
                    <label className="block text-sm font-medium text-sky-900 mb-1">
                      Monto del ajuste
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={cashAdjustment}
                      onChange={(e) => setCashAdjustment(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 border border-sky-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                    <p className="text-xs text-sky-800/80 mt-1">
                      Positivo: entra plata · Negativo: sale / corrige faltante
                    </p>
                  </div>
                ) : null}
              </div>

              <form
                onSubmit={handleSave}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-5"
              >
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Arqueo de caja</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Al cerrar, el sistema guarda el saldo final y genera el reporte oficial de
                    cierre de caja (PDF).
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Saldo inicial
                    </p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {formatMoney(openingNum)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {openingEditable
                        ? 'Configúrelo con la opción de arriba (solo primera vez).'
                        : openingFromDate
                          ? `Desde cierre del ${formatDate(openingFromDate)}`
                          : 'Desde cierre anterior'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3">
                    <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">
                      Efectivo cobrado
                    </p>
                    <p className="text-lg font-semibold text-emerald-900 mt-1">
                      {formatMoney(cashSales)}
                    </p>
                    <p className="text-xs text-emerald-700/80 mt-1">Ventas en efectivo del día</p>
                  </div>
                  <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3">
                    <p className="text-xs font-medium text-teal-700 uppercase tracking-wide">
                      Saldo final (sistema)
                    </p>
                    <p className="text-lg font-semibold text-teal-900 mt-1">
                      {formatMoney(systemClosing)}
                    </p>
                    <p className="text-xs text-teal-700/80 mt-1">
                      {formatMoney(openingNum)} + {formatMoney(cashSales)}
                      {hasAdjustment
                        ? ` ${adjustmentNum >= 0 ? '+' : '−'} ${formatMoney(Math.abs(adjustmentNum))}`
                        : ''}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg px-4 py-3 border bg-gray-50 border-gray-200 text-gray-600">
                  <p className="text-sm font-medium">
                    Este saldo final se usará como saldo inicial del día siguiente. Si hay
                    descuadre, use la opción «Registrar ajuste».
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Observaciones del arqueo o del ajuste…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {liquidationSaved ? (
                    <button
                      type="button"
                      onClick={() => handleDownloadReport()}
                      disabled={pdfDownloading || saving}
                      className="px-4 py-2.5 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                    >
                      {pdfDownloading ? 'Generando PDF…' : 'Descargar reporte de cierre'}
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    disabled={saving || pdfDownloading}
                    className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
                  >
                    {saving ? 'Cerrando…' : 'Cerrar liquidación'}
                  </button>
                </div>
              </form>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Detalle de ventas del día</h2>
                {!data.orders?.length ? (
                  <p className="text-sm text-gray-500 py-6 text-center">
                    No hay ventas emitidas en este turno.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 border-b">
                          <th className="py-2 pr-3">Venta</th>
                          <th className="py-2 pr-3">Cliente</th>
                          <th className="py-2 pr-3">Medio</th>
                          <th className="py-2 pr-3">N° operación</th>
                          <th className="py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.orders.map((order) => (
                          <tr key={order.id} className="border-b border-gray-100">
                            <td className="py-2 pr-3">
                              <div className="font-medium text-gray-800">{order.code}</div>
                              {order.voucher ? (
                                <div className="text-xs font-mono text-gray-500">{order.voucher}</div>
                              ) : null}
                            </td>
                            <td className="py-2 pr-3 text-gray-700">{order.client_name || '—'}</td>
                            <td className="py-2 pr-3 font-medium">
                              {getVentasPaymentMethodLabel(order.payment_method)}
                            </td>
                            <td className="py-2 pr-3 font-mono text-gray-700">
                              {order.operation_number || '—'}
                            </td>
                            <td className="py-2 text-right font-semibold">
                              {formatMoney(order.total_amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
}

/** Redirección de la ruta antigua en Reportes. */
export function VentasCashByShiftRedirect() {
  const { loaded, ventasCashLiquidationEnabled } = useModuleSettings();
  if (!loaded) return null;
  if (!ventasCashLiquidationEnabled) {
    return <Navigate to="/ventas/orders" replace />;
  }
  return <Navigate to="/ventas/caja" replace />;
}
